import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '../lib/supabaseClient';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { formatPhone } from './QualifyLeadDialog';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

interface ScheduledSms {
  id: string;
  to_phone: string;
  quote_id: string | null;
  body: string;
  status: 'scheduled' | 'sending' | 'sent' | 'skipped' | 'cancelled' | 'failed';
  sender_name: string | null;
  next_check_at: string;
  skip_reason: string | null;
  cancelled_by: string | null;
  updated_at: string;
}

const SKIP_WORDS: Record<string, string> = {
  staff_already_texted: 'someone texted them first',
  call_never_ended: 'the call never ended',
  lead_disqualified: 'the lead was disqualified',
};

/** What the family will read: {quote_link} becomes the link the sender fills in. */
function shownBody(r: { body: string; quote_id: string | null }): string {
  return r.quote_id
    ? r.body.split('{quote_link}').join(`https://cleocremation.com/app/${r.quote_id}/`)
    : r.body;
}

/** "8:00" today, "Thursday 8:00" otherwise, Toronto time. */
function whenLabel(iso: string): string {
  const at = new Date(iso);
  const tz = 'America/Toronto';
  const time = at.toLocaleTimeString('en-CA', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  const sameDay =
    at.toLocaleDateString('en-CA', { timeZone: tz }) ===
    new Date().toLocaleDateString('en-CA', { timeZone: tz });
  return sameDay ? time : `${at.toLocaleDateString('en-CA', { timeZone: tz, weekday: 'long' })} ${time}`;
}

/**
 * The qualify follow-up text (T-190) until it goes: one row, "Scheduled",
 * Edit and Cancel. Once sent it is an ordinary text in the feed, so only the
 * waiting and the didn't-go states show here.
 */
export function ScheduledSmsRow({ leadId }: { leadId: string }) {
  const notify = useNotify();
  const [rows, setRows] = useState<ScheduledSms[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const since = new Date(Date.now() - 24 * 3600_000).toISOString();
    const { data } = await supabase
      .from('scheduled_sms')
      .select('id, to_phone, quote_id, body, status, sender_name, next_check_at, skip_reason, cancelled_by, updated_at')
      .eq('lead_id', leadId)
      .neq('status', 'sent')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(3);
    setRows((data as ScheduledSms[]) || []);
  }, [leadId]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const call = async (path: string, method: 'POST' | 'PATCH', body?: unknown) => {
    setBusy(true);
    try {
      const res = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Failed (${res.status})`);
      }
      return true;
    } catch (e: any) {
      notify(e.message || 'Something went wrong', { type: 'warning' });
      return false;
    } finally {
      setBusy(false);
      void load();
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="grid gap-2 px-4 pb-2">
      {rows.map((r) =>
        r.status === 'scheduled' || r.status === 'sending' ? (
          <div key={r.id} className="grid gap-1.5 rounded-lg border border-dashed border-primary bg-accent p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {r.status === 'sending' ? 'Sending' : 'Scheduled'}
              </span>
              <span>
                Text to {formatPhone(r.to_phone)}
                {r.sender_name ? ` · ${r.sender_name}` : ''}
              </span>
              <span>· sends about 5 min after the call ends{new Date(r.next_check_at).getTime() - Date.now() > 3600_000 ? `, not before ${whenLabel(r.next_check_at)}` : ''}</span>
            </div>
            {editing === r.id ? (
              <>
                <Textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Edit text" />
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    disabled={busy || !draft.trim()}
                    onClick={() =>
                      void call(`/qualify-sms/${r.id}`, 'PATCH', { body: draft }).then((ok) => ok && setEditing(null))
                    }
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Discard</Button>
                </div>
              </>
            ) : (
              <>
                <p className="whitespace-pre-wrap break-words text-[13.5px]">{shownBody(r)}</p>
                {r.status === 'scheduled' && (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        setDraft(r.body);
                        setEditing(r.id);
                      }}
                    >
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => void call(`/qualify-sms/${r.id}/cancel`, 'POST')}>
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div key={r.id} className="text-xs text-muted-foreground">
            {r.status === 'cancelled'
              ? `Text cancelled${r.cancelled_by ? ` by ${r.cancelled_by}` : ''}.`
              : r.status === 'failed'
                ? 'Text could not be sent.'
                : `Text not sent: ${SKIP_WORDS[r.skip_reason || ''] || r.skip_reason || 'skipped'}.`}
          </div>
        ),
      )}
    </div>
  );
}
